import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

export type ConsultingRoomZoneType = 'consulting_room' | 'xray' | 'ultrasound' | 'other';

interface WorkstationOption {
  id: string;
  display_name: string;
}

const schema = z.object({
  name: z.string().trim().min(1).max(80),
  display_order: z.coerce.number().int().min(0),
  zone_type: z.enum(['consulting_room', 'xray', 'ultrasound', 'other']),
  workstation_id: z.string().optional(),
});

export type ConsultingRoomFormValues = z.infer<typeof schema>;

interface ConsultingRoomFormDialogProps {
  title: string;
  initial_values?: Partial<ConsultingRoomFormValues>;
  workstation_options: readonly WorkstationOption[];
  submitting: boolean;
  onSubmit: (values: ConsultingRoomFormValues) => Promise<void>;
  onCancel: () => void;
}

export function ConsultingRoomFormDialog({
  title,
  initial_values,
  workstation_options,
  submitting,
  onSubmit,
  onCancel,
}: ConsultingRoomFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const form = useForm<ConsultingRoomFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: initial_values?.name ?? '',
      display_order: initial_values?.display_order ?? 0,
      zone_type: initial_values?.zone_type ?? 'consulting_room',
      workstation_id: initial_values?.workstation_id ?? '',
    },
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur"
    >
      <Card className="w-full max-w-md">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit((values) => onSubmit(values))(event);
          }}
          noValidate
        >
          <CardHeader>
            <CardTitle>{title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="room_name">{t('settings_ops.consulting_rooms.name')}</Label>
              <Input id="room_name" maxLength={80} {...form.register('name')} />
              {form.formState.errors.name ? (
                <p className="text-destructive text-sm">{form.formState.errors.name.message}</p>
              ) : null}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="room_display_order">
                  {t('settings_ops.consulting_rooms.display_order')}
                </Label>
                <Input
                  id="room_display_order"
                  type="number"
                  min={0}
                  step={1}
                  {...form.register('display_order')}
                />
                {form.formState.errors.display_order ? (
                  <p className="text-destructive text-sm">
                    {form.formState.errors.display_order.message}
                  </p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="room_zone_type">
                  {t('settings_ops.consulting_rooms.zone_type')}
                </Label>
                <select
                  id="room_zone_type"
                  {...form.register('zone_type')}
                  className="border-input bg-background focus:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2"
                >
                  <option value="consulting_room">
                    {t('settings_ops.consulting_rooms.zone_type_consulting_room')}
                  </option>
                  <option value="xray">{t('settings_ops.consulting_rooms.zone_type_xray')}</option>
                  <option value="ultrasound">
                    {t('settings_ops.consulting_rooms.zone_type_ultrasound')}
                  </option>
                  <option value="other">
                    {t('settings_ops.consulting_rooms.zone_type_other')}
                  </option>
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="room_workstation">{t('settings_ops.workstation')}</Label>
              <select
                id="room_workstation"
                {...form.register('workstation_id')}
                className="border-input bg-background focus:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2"
              >
                <option value="">{t('settings_ops.workstation_none')}</option>
                {workstation_options.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.display_name}
                  </option>
                ))}
              </select>
            </div>
          </CardContent>
          <CardFooter className="justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('common.loading') : t('common.save')}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
